import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebaseConfig';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';

const AdminDashboard = ({ onBack }) => {
  const [pools, setPools] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);

  // Filtros de data
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [poolsSnap, champsSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, "pools")),
          getDocs(collection(db, "championships")),
          getDocs(collection(db, "users"))
        ]);

        setPools(poolsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setChampionships(champsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Erro ao carregar dados do admin dashboard", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const champMap = useMemo(() => {
    const map = {};
    championships.forEach(c => {
      map[c.id] = c;
    });
    return map;
  }, [championships]);

  const userMap = useMemo(() => {
    const map = {};
    users.forEach(u => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  // Filtra globalmente para considerar apenas bolões com mais de 3 participantes
  const validPools = useMemo(() => 
    pools.filter(p => (p.participants?.length || 0) > 3)
  , [pools]);

  const filteredPools = useMemo(() => {
    if (!selectedType) return validPools;
    return validPools.filter(p => {
      const type = champMap[p.championshipId]?.type || 'Desconhecido';
      return type === selectedType;
    });
  }, [validPools, champMap, selectedType]);

  const sortedFilteredPools = useMemo(() => {
    return [...filteredPools].sort((a, b) => (b.participants?.length || 0) - (a.participants?.length || 0));
  }, [filteredPools]);

  // Cálculos de Bolões
  const activePools = useMemo(() => 
    filteredPools.filter(p => champMap[p.championshipId]?.status !== 'finished')
  , [filteredPools, champMap]);

  const closedPools = useMemo(() => 
    filteredPools.filter(p => champMap[p.championshipId]?.status === 'finished')
  , [filteredPools, champMap]);

  const poolsByTypeData = useMemo(() => {
    const counts = {};
    validPools.forEach(p => {
      const type = champMap[p.championshipId]?.type || 'Desconhecido';
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.keys(counts).map(key => ({ type: key, quantidade: counts[key] }));
  }, [validPools, champMap]);

  // Cálculo de Logins
  const loginChartData = useMemo(() => {
    const counts = {};
    users.forEach(u => {
      if (u.lastLogin) {
        // Assume lastLogin is ISO string, extraindo YYYY-MM-DD
        const dateStr = u.lastLogin.substring(0, 10); 
        if ((!startDate || dateStr >= startDate) && (!endDate || dateStr <= endDate)) {
          counts[dateStr] = (counts[dateStr] || 0) + 1;
        }
      }
    });
    return Object.keys(counts).sort().map(date => ({
      date: date.split('-').reverse().join('/'), // DD/MM/YYYY
      logins: counts[date]
    }));
  }, [users, startDate, endDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-teal-400">Dashboard Administrativo</h1>
        <button 
          onClick={onBack}
          className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Voltar
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700 hover:border-teal-500 transition">
          <h3 className="text-gray-400 font-medium mb-2">Bolões Ativos</h3>
          <p className="text-4xl font-bold text-white">{activePools.length}</p>
          <p className="text-sm text-teal-400 mt-2">Mais de 3 participantes</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700 hover:border-blue-500 transition">
          <h3 className="text-gray-400 font-medium mb-2">Bolões Encerrados</h3>
          <p className="text-4xl font-bold text-white">{closedPools.length}</p>
          <p className="text-sm text-blue-400 mt-2">Campeonatos finalizados</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700 hover:border-purple-500 transition">
          <h3 className="text-gray-400 font-medium mb-2">Total de Usuários</h3>
          <p className="text-4xl font-bold text-white">{users.length}</p>
          <p className="text-sm text-purple-400 mt-2">Registrados na plataforma</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Gráfico de Tipos de Bolão */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-teal-300">
              Tipos de Bolões {selectedType && <span className="text-sm text-blue-400 bg-blue-900/30 px-2 py-1 rounded">(Filtro: {selectedType})</span>}
            </h3>
            {selectedType && (
              <button onClick={() => setSelectedType(null)} className="text-sm text-gray-400 hover:text-white underline">Limpar Filtro</button>
            )}
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={poolsByTypeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="type" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" allowDecimals={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  itemStyle={{ color: '#2DD4BF' }}
                  cursor={{fill: '#374151', opacity: 0.4}}
                />
                <Bar 
                  dataKey="quantidade" 
                  fill="#2DD4BF" 
                  radius={[4, 4, 0, 0]} 
                  onClick={(data) => setSelectedType(prev => prev === data.type ? null : data.type)} 
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico de Usuários Logados */}
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h3 className="text-xl font-semibold text-teal-300">Logins Diários</h3>
            <div className="flex gap-2 text-sm">
              <input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white focus:outline-none focus:border-teal-500"
              />
              <span className="self-center">até</span>
              <input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>
          <div className="h-72">
            {loginChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={loginChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    itemStyle={{ color: '#60A5FA' }}
                  />
                  <Line type="monotone" dataKey="logins" stroke="#60A5FA" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                Nenhum login registrado neste período.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabela de Detalhes dos Bolões */}
      <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700 overflow-x-auto">
        <h3 className="text-xl font-semibold mb-6 text-teal-300">Detalhamento dos Bolões</h3>
        <table className="w-full text-left border-collapse min-w-max">
          <thead>
            <tr className="border-b border-gray-700 text-gray-400">
              <th className="p-3 font-medium">Nome do Bolão</th>
              <th className="p-3 font-medium">Campeonato</th>
              <th className="p-3 font-medium">Data Criação</th>
              <th className="p-3 font-medium">Criador</th>
              <th className="p-3 font-medium text-center">Usuários</th>
              <th className="p-3 font-medium text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedFilteredPools.map(pool => {
              const champ = champMap[pool.championshipId] || {};
              const adminUser = userMap[pool.adminId] || {};
              
              // Se não existir createdAt, usamos o fallback solicitado
              let createdDateStr = "01/01/2026";
              if (pool.createdAt) {
                try {
                  // Se for um timestamp do Firestore
                  if (pool.createdAt.toDate) {
                    createdDateStr = pool.createdAt.toDate().toLocaleDateString('pt-BR');
                  } else {
                    // Tenta criar data da string/number
                    createdDateStr = new Date(pool.createdAt).toLocaleDateString('pt-BR');
                  }
                } catch(e) {}
              }

              return (
                <tr key={pool.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition">
                  <td className="p-3 font-medium text-white">{pool.name}</td>
                  <td className="p-3 text-gray-300">
                    <div>{champ.name || 'Desconhecido'}</div>
                    <div className="text-xs text-gray-500">{champ.type || 'Sem tipo'}</div>
                  </td>
                  <td className="p-3 text-gray-400">{createdDateStr}</td>
                  <td className="p-3 text-gray-300">{adminUser.displayName || adminUser.email || 'Desconhecido'}</td>
                  <td className="p-3 text-center">
                    <span className="bg-teal-900/50 text-teal-300 py-1 px-3 rounded-full text-sm font-semibold">
                      {pool.participants?.length || 0}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    {champ.status === 'finished' ? (
                      <span className="text-blue-400 text-sm">Encerrado</span>
                    ) : (
                      <span className="text-teal-400 text-sm">Ativo</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {sortedFilteredPools.length === 0 && (
              <tr>
                <td colSpan="6" className="p-6 text-center text-gray-500">Nenhum bolão encontrado com mais de 3 participantes.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default AdminDashboard;
